import { Dialog } from 'primereact/dialog';
import { useState, useEffect } from 'react';
import { useGetPlatformsQuery, useGetAccountsByPlatformQuery, useGetCampaignsByAccountQuery } from 'src/redux/admin/reporting/reporting.endpoints';
import { Skeleton } from 'src/components/ui/skeleton';
import { useDeleteClientAccessMutation, useGetClientAccessQuery, useUpdateClientAccessMutation, useUpdateClientMutation } from 'src/redux/organization/organization.endpoints';
import { Client, ClientView } from 'src/redux/organization/organization.types';
import { Button } from 'src/components/ui/button';
import { Search } from "lucide-react";
import { Input } from "src/components/ui/input";

interface AssignCampaignsModalProps {
  isOpen: boolean;
  onClose: () => void;
  client: Client;
  onContinue?: () => void;
}

interface SearchBarProps {
  value: string;
  onChange: (value: string) => void;
}

const SearchBar = ({ value, onChange }: SearchBarProps) => (
  <div className="px-4 my-3">
    <div className="relative flex-1 w-full">
      <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 h-3.5 w-3.5" />
      <Input
        type="text"
        placeholder="Search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="pl-8 bg-white rounded-lg placeholder:text-gray-400 h-8 w-full text-sm border border-gray-200"
      />
    </div>
  </div>
);

export const AssignCampaignsModal = ({ isOpen, onClose, client, onContinue }: AssignCampaignsModalProps) => {
  const [currentPlatform, setCurrentPlatform] = useState<string>('');
  const [currentAccount, setCurrentAccount] = useState<string>('');
  const [selectedCampaigns, setSelectedCampaigns] = useState<Record<string, string[]>>({});
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [localClient, setLocalClient] = useState<Client>(client);
  const [platformSearch, setPlatformSearch] = useState("");
  const [accountSearch, setAccountSearch] = useState("");
  const [campaignSearch, setCampaignSearch] = useState("");

  const customSort = (a: string, b: string): number => {
    const aStartsWithNumber = /^\d/.test(a);
    const bStartsWithNumber = /^\d/.test(b);
    if (aStartsWithNumber && !bStartsWithNumber) return 1;
    if (!aStartsWithNumber && bStartsWithNumber) return -1;
    return a.localeCompare(b);
  };

  const { data: platforms = [], isFetching: isFetchingPlatforms } = useGetPlatformsQuery({});
  const { data: accounts = [], isFetching: isFetchingAccounts } = useGetAccountsByPlatformQuery({ source: currentPlatform }, { skip: !currentPlatform });
  const { data: campaigns = [], isFetching: isFetchingCampaigns } = useGetCampaignsByAccountQuery({ source: currentPlatform, accountId: [Number(currentAccount)] }, { skip: !currentAccount });
  const { data: clientAccess = [], isFetching: isFetchingAccess } = useGetClientAccessQuery(client.ext_client_id);
  const [updateAccess, { isLoading: isUpdating }] = useUpdateClientAccessMutation();
  const [deleteClientAccess, { isLoading: isDeleting }] = useDeleteClientAccessMutation();
  const [updateClient] = useUpdateClientMutation();

  useEffect(() => {
    setLocalClient(client);
  }, [client]);

  // Initialize selected campaigns from client access data
  useEffect(() => {
    if (clientAccess.length > 0) {
      const initialSelected: Record<string, string[]> = {};
      clientAccess.forEach(platform => {
        platform.accounts.forEach(account => {
          const key = `${platform.source}_${account.account_id}`;
          initialSelected[key] = account.campaigns;
        });
      });
      setSelectedCampaigns(initialSelected);
    }
  }, [clientAccess]);

  // Set initial platform if none selected
  useEffect(() => {
    if (!isFetchingPlatforms && platforms.length > 0 && !currentPlatform) {
      setCurrentPlatform(platforms[0]);
    }
  }, [platforms, currentPlatform, isFetchingPlatforms]);

  // Set initial account if none selected
  useEffect(() => {
    if (!isFetchingAccounts && accounts.length > 0 && !currentAccount) {
      const sortedAccounts = [...accounts].sort((a, b) => customSort(a.name, b.name));
      setCurrentAccount(sortedAccounts[0].id.toString());
    }
  }, [accounts, currentAccount, isFetchingAccounts]);

  const createDefaultClientView = (accountId: string, accountName: string): ClientView => ({
    account_ext_id: accountId,
    account_name: accountName,
    platform: currentPlatform,
    metrics: [],
    graph_one: {
      metric_one: null,
      metric_two: null,
    },
    graph_two: {
      metric_one: null,
      metric_two: null,
    },
    graph_three: {
      metric_one: null,
      metric_two: null,
    },
  });

  const handleUpdateAccess = async(campaignIds: string[]) => {
    if (!currentPlatform || !currentAccount) return;

    const key = `${currentPlatform}_${currentAccount}`;
    const currentSelected = selectedCampaigns[key] || [];

    setSelectedCampaigns(prev => ({
      ...prev,
      [key]: campaignIds,
    }));

    setSaveStatus('saving');
    try {
      await updateAccess({
        ext_client_id: localClient.ext_client_id,
        source: currentPlatform,
        account: currentAccount,
        campaigns: campaignIds,
      });

      // If this is the first time selecting campaigns for this account, add a new ClientView
      if (currentSelected.length === 0) {
        const currentAccountData = accounts.find(acc => acc.id.toString() === currentAccount);
        if (currentAccountData) {
          const newView = createDefaultClientView(currentAccount, currentAccountData.name);
          const updatedViews = [...localClient.views, newView];
          const updatedClient = {
            ...localClient,
            views: updatedViews,
          };
          await updateClient({
            clientId: localClient.ext_client_id,
            data: updatedClient,
          });
          setLocalClient(updatedClient);
        }
      }

      setSaveStatus('saved');
    } catch (error) {
      console.error('Failed to update campaign access:', error);
      // Revert the selection if the update fails
      setSelectedCampaigns(prev => ({
        ...prev,
        [key]: currentSelected,
      }));
      setSaveStatus('idle');
    }
  };

  const handleCampaignToggle = async(campaignId: string) => {
    if (!currentPlatform || !currentAccount) return;

    const key = `${currentPlatform}_${currentAccount}`;
    const currentSelected = selectedCampaigns[key] || [];
    const isDeselecting = currentSelected.includes(campaignId);
    const newSelected = isDeselecting
      ? currentSelected.filter(id => id !== campaignId)
      : [...currentSelected, campaignId];

    if (isDeselecting) {
      setSelectedCampaigns(prev => ({
        ...prev,
        [key]: newSelected,
      }));

      setSaveStatus('saving');
      try {
        await deleteClientAccess({
          ext_client_id: localClient.ext_client_id,
          source: currentPlatform,
          account: currentAccount,
          campaign: campaignId,
        });

        // If this was the last campaign for this account, remove the corresponding ClientView
        if (newSelected.length === 0) {
          const updatedViews = localClient.views.filter(view => view.account_ext_id !== currentAccount);
          const updatedClient = {
            ...localClient,
            views: updatedViews,
          };
          await updateClient({
            clientId: localClient.ext_client_id,
            data: updatedClient,
          });
          setLocalClient(updatedClient);
        }
        setSaveStatus('saved');
      } catch (error) {
        console.error('Failed to update campaign access:', error);
        // Revert the selection if the update fails
        setSelectedCampaigns(prev => ({
          ...prev,
          [key]: currentSelected,
        }));
        setSaveStatus('idle');
      }
    } else {
      await handleUpdateAccess(newSelected);
    }
  };

  const handleSelectAll = async() => {
    if (currentPlatform && currentAccount) {
      const allCampaignIds = campaigns.map(campaign => campaign.id.toString());
      await handleUpdateAccess(allCampaignIds);
    }
  };

  const isCampaignSelected = (campaignId: string) => {
    const key = `${currentPlatform}_${currentAccount}`;
    return (selectedCampaigns[key] || []).includes(campaignId);
  };

  const hasSelectedCampaigns = (platform: string, accountId?: string) => {
    if (accountId) {
      const key = `${platform}_${accountId}`;
      return (selectedCampaigns[key] || []).length > 0;
    }
    const platformHasCampaigns = Object.entries(selectedCampaigns).some(([key, campaigns]) => {
      const platformKey = key.substring(0, key.lastIndexOf('_'));
      return platformKey === platform && campaigns.length > 0;
    });
    return platformHasCampaigns;
  };

  const dialogHeader = (
    <div className="flex items-center justify-between w-full">
      <span className="text-lg font-medium">
        Step 1: Assign Campaigns to Client
      </span>
      <div className="flex items-center gap-2">
        {(isUpdating || isDeleting || saveStatus === 'saving') && (
          <span className="text-base text-gray-500 h-6 leading-none mr-4">
            Saving...
          </span>
        )}
        {saveStatus === 'saved' && !isUpdating && !isDeleting && (
          <span className="text-base text-gray-500 h-6 leading-none mr-4">
            Saved
          </span>
        )}
      </div>
    </div>
  );

  const filteredPlatforms = platforms
    .filter((platform) =>
      platform.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
        .toLowerCase()
        .includes(platformSearch.toLowerCase())
    );

  const filteredAccounts = accounts
    .filter((account) =>
      account.name.toLowerCase().includes(accountSearch.toLowerCase())
    )
    .sort((a, b) => customSort(a.name, b.name));

  const filteredCampaigns = campaigns
    .filter((campaign) =>
      campaign.name.toLowerCase().includes(campaignSearch.toLowerCase())
    )
    .sort((a, b) => customSort(a.name, b.name));

  return (
    <Dialog
      visible={isOpen}
      onHide={onClose}
      header={dialogHeader}
      style={{ width: '90vw', maxWidth: '900px' }}
      contentStyle={{ paddingLeft: 8, paddingRight: 8 }}
      draggable={false}
      resizable={false}
      footer={(
        <div className="flex justify-end w-full">
          <Button
            onClick={onContinue}
            variant="primary"
            size="lg"
            className="w-full md:w-auto"
            disabled={
              isFetchingAccess ||
              isUpdating ||
              isDeleting ||
              saveStatus === 'saving' ||
              isFetchingPlatforms ||
              isFetchingAccounts ||
              isFetchingCampaigns
            }
          >
            Next
          </Button>
        </div>
      )}
    >
      <div className="flex flex-col md:flex-row h-[70vh] min-h-[400px] w-full gap-2 md:gap-4">
        {/* Platforms Section */}
        <div className="flex-1 min-h-0 w-full md:w-1/3 min-w-0 border-b md:border-b-0 md:border-r border-gray-200 flex flex-col">
          <h3 className="font-medium px-4 pt-4">
            Platforms
          </h3>
          <SearchBar
            value={platformSearch}
            onChange={setPlatformSearch}
          />
          <div className="flex-1 overflow-y-auto px-4">
            {isFetchingPlatforms ? (
              <div className="space-y-2">
                {[1, 2, 3].map(i => (
                  <Skeleton
                    key={i}
                    className="h-8"
                  />
                ))}
              </div>
            ) : (
              <div className="space-y-1">
                {filteredPlatforms.map((platform, index) => (
                  <button
                    key={`${currentPlatform}_${platform}_${index}`}
                    onClick={() => {
                      setCurrentPlatform(platform);
                      setCurrentAccount('');
                    }}
                    className={`w-full text-left text-sm px-2 py-1 md:text-base md:px-3 md:py-1 rounded-lg flex items-center justify-between ${
                      currentPlatform === platform
                        ? 'bg-gray-100 font-medium'
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      {hasSelectedCampaigns(platform) && (
                        <span className="text-black text-xs">
                          ✔️
                        </span>
                      )}
                      {platform.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                    </span>
                    {currentPlatform === platform && (
                      <span className="text-gray-500">
                        &gt;
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Accounts Section */}
        <div className="flex-1 min-h-0 w-full md:w-1/3 min-w-0 border-b md:border-b-0 md:border-r border-gray-200 flex flex-col">
          <h3 className="font-medium px-4 pt-4">
            Accounts
          </h3>
          <SearchBar
            value={accountSearch}
            onChange={setAccountSearch}
          />
          <div className="flex-1 overflow-y-auto px-4">
            {isFetchingAccounts ? (
              <div className="space-y-2">
                {[1, 2, 3].map(i => (
                  <Skeleton
                    key={i}
                    className="h-8"
                  />
                ))}
              </div>
            ) : (
              <div className="space-y-1">
                {filteredAccounts.map((account, index) => (
                  <button
                    key={`${currentPlatform}_${account.id}_${index}`}
                    onClick={() => setCurrentAccount(account.id.toString())}
                    className={`w-full text-left text-sm px-2 py-1 md:text-base md:px-3 md:py-1 rounded-lg flex items-center justify-between ${
                      currentAccount === account.id.toString()
                        ? 'bg-gray-100 font-medium'
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    <span className="flex items-center gap-2">
                      {hasSelectedCampaigns(currentPlatform, account.id.toString()) && (
                        <span className="text-black text-xs">
                          ✔️
                        </span>
                      )}
                      {account.name}
                    </span>
                    {currentAccount === account.id.toString() && (
                      <span className="text-gray-500">
                        &gt;
                      </span>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Campaigns Section */}
        <div className="flex-1 min-h-0 w-full md:w-1/3 min-w-0 flex flex-col">
          <div className="flex items-center justify-between px-4 pt-4">
            <h3 className="font-medium">
              Campaigns
            </h3>
            <button
              onClick={handleSelectAll}
              className="text-sm text-gray-500 hover:text-gray-700"
              type="button"
              disabled={isUpdating || !currentPlatform || !currentAccount}
            >
              Select all
            </button>
          </div>
          <SearchBar
            value={campaignSearch}
            onChange={setCampaignSearch}
          />
          <div className="flex-1 overflow-y-auto px-4">
            {isFetchingCampaigns ? (
              <div className="space-y-2">
                {[1, 2, 3].map(i => (
                  <Skeleton
                    key={i}
                    className="h-8"
                  />
                ))}
              </div>
            ) : (
              <div className="space-y-1">
                {filteredCampaigns.map((campaign, index) => (
                  <label
                    key={`${currentPlatform}_${currentAccount}_${campaign.id}_${index}`}
                    className="flex items-center space-x-3 text-sm px-2 py-1 md:text-base md:px-3 md:py-1 rounded-lg hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={isCampaignSelected(campaign.id.toString())}
                      onChange={() => handleCampaignToggle(campaign.id.toString())}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      disabled={isUpdating}
                    />
                    <span>
                      {campaign.name}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </Dialog>
  );
};